import { dist } from "vector";
import {
  definedMultipliers,
  getScaleLabel,
} from "@local/extensions/utilities/scales.js";

// Within this relative deviation of a standard scale, the difference is
// click noise on a true-to-scale sheet and the standard scale is suggested.
// Beyond it the measurement stands: a fit-to-page shrink (typically 3-10%)
// must never be snapped back to the scale printed in the title block.
const SNAP_TOLERANCE = 0.01;

/** Paper inches between two picked points in tile pixels (300dpi). */
function paperLength(pts, dpi = 300) {
  return dist(pts[0], pts[1]) / dpi;
}

/**
 * Compute the page scale implied by a measured paper length and the
 * dimension value written on the drawing. Returns the exact ratio as a
 * custom "1:X" scale, the nearest standard scale with its relative
 * deviation, and which of the two should be preselected.
 */
function scaleFromDimension({ paperInches, realInches }) {
  if (!Number.isFinite(paperInches) || paperInches <= 0) return null;
  if (!Number.isFinite(realInches) || realInches <= 0) return null;

  const ratio = realInches / paperInches;

  let nearest = null;
  for (const [value, mult] of Object.entries(definedMultipliers)) {
    const deviation = Math.abs(ratio - mult) / mult;
    if (!nearest || deviation < nearest.deviation) {
      nearest = { value, label: getScaleLabel(value), deviation };
    }
  }

  // No locale grouping: "1:2,405" would not parse back through multiplier().
  const exactValue = `1:${ratio.toLocaleString("en-US", {
    useGrouping: false,
    maximumFractionDigits: 2,
  })}`;

  return {
    ratio,
    exact: { value: exactValue, label: exactValue },
    nearest,
    suggested: nearest.deviation <= SNAP_TOLERANCE ? "nearest" : "exact",
  };
}

export { paperLength, scaleFromDimension };
